import React, { useEffect, useRef } from 'react';
import { useI18n } from '../../context/I18nContext';
import TextArea from '../ui/TextArea';
import {
  EMAIL_SIGNATURE_MAX_HTML_CHARS,
  EMAIL_SIGNATURE_MAX_PLAIN_CHARS,
  getEmailSignatureSizeError,
  htmlToPlainText,
  prepareEmailSignatureHtml,
  sanitizeEmailSignatureHtml,
} from '../../utils/emailSignatureHtml';

interface EmailSignatureEditorProps {
  html: string;
  plain: string;
  onChange: (next: { html: string; plain: string }) => void;
  onTooLarge?: (kind: 'html' | 'plain') => void;
  disabled?: boolean;
  highlighted?: boolean;
  highlightClassName?: string;
}

/**
 * Paste-friendly signature editor: rich clipboard HTML is kept;
 * otherwise plain text via the shared TextArea.
 */
const EmailSignatureEditor: React.FC<EmailSignatureEditorProps> = ({
  html,
  plain,
  onChange,
  onTooLarge,
  disabled = false,
  highlighted = false,
  highlightClassName = '',
}) => {
  const { t } = useI18n();
  const editorRef = useRef<HTMLDivElement>(null);
  const hasHtml = Boolean(html.trim());
  const pasteSeqRef = useRef(0);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || !hasHtml) return;
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
  }, [html, hasHtml]);

  useEffect(() => {
    if (!highlighted) return;
    const el = document.getElementById('settings-email-signature-field');
    if (el instanceof HTMLElement && typeof el.focus === 'function') {
      el.focus({ preventScroll: true });
    }
  }, [highlighted]);

  const emitFromHtml = async (rawHtml: string, normalizeImages: boolean) => {
    const seq = ++pasteSeqRef.current;
    const clean = normalizeImages
      ? await prepareEmailSignatureHtml(rawHtml)
      : sanitizeEmailSignatureHtml(rawHtml);
    if (seq !== pasteSeqRef.current) return;

    const nextPlain = htmlToPlainText(clean) || plain;
    const sizeError = getEmailSignatureSizeError({ html: clean, plain: nextPlain });
    if (sizeError) {
      onTooLarge?.(sizeError === 'html_too_large' ? 'html' : 'plain');
      return;
    }
    onChange({
      html: clean,
      plain: nextPlain,
    });
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const clipboardHtml = e.clipboardData.getData('text/html');
    const clipboardText = e.clipboardData.getData('text/plain');
    if (clipboardHtml.trim()) {
      e.preventDefault();
      void emitFromHtml(clipboardHtml, true);
      return;
    }
    if (!(e.target instanceof HTMLTextAreaElement) && clipboardText.trim()) {
      e.preventDefault();
      if (clipboardText.length > EMAIL_SIGNATURE_MAX_PLAIN_CHARS) {
        onTooLarge?.('plain');
        return;
      }
      onChange({ html: '', plain: clipboardText });
    }
  };

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    void emitFromHtml(el.innerHTML, false);
  };

  const handlePlainChange = (value: string) => {
    if (value.length > EMAIL_SIGNATURE_MAX_PLAIN_CHARS) {
      onTooLarge?.('plain');
      onChange({ html: '', plain: value.slice(0, EMAIL_SIGNATURE_MAX_PLAIN_CHARS) });
      return;
    }
    onChange({ html: '', plain: value });
  };

  const nearHtmlLimit = hasHtml && html.length > EMAIL_SIGNATURE_MAX_HTML_CHARS * 0.8;
  const nearPlainLimit = !hasHtml && plain.length > EMAIL_SIGNATURE_MAX_PLAIN_CHARS * 0.8;
  const sizeLabel = hasHtml
    ? `${html.length.toLocaleString()} / ${EMAIL_SIGNATURE_MAX_HTML_CHARS.toLocaleString()}`
    : `${plain.length.toLocaleString()} / ${EMAIL_SIGNATURE_MAX_PLAIN_CHARS.toLocaleString()}`;

  const fieldHighlight = highlighted
    ? `transition-colors duration-500 ${highlightClassName}`
    : 'transition-colors duration-500';

  return (
    <div className="space-y-1.5">
      {hasHtml ? (
        <>
          <div
            id="settings-email-signature-field"
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onPaste={handlePaste}
            onInput={handleInput}
            className={`min-h-[4.5rem] overflow-x-auto rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-base sm:text-sm shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent [&_img]:!h-auto [&_img]:max-w-full ${fieldHighlight}`}
            aria-label={t('settings.emailSignature')}
          />
          {nearHtmlLimit ? (
            <p className="text-xs text-gray-400 tabular-nums text-right">{sizeLabel}</p>
          ) : null}
        </>
      ) : (
        <>
          <TextArea
            id="settings-email-signature-field"
            value={plain}
            onChange={(e) => handlePlainChange(e.target.value)}
            onPaste={handlePaste}
            placeholder={t('settings.emailSignaturePlaceholder')}
            rows={3}
            disabled={disabled}
            aria-label={t('settings.emailSignature')}
            maxLength={EMAIL_SIGNATURE_MAX_PLAIN_CHARS}
            className={fieldHighlight}
          />
          {nearPlainLimit ? (
            <p className="text-xs text-gray-400 tabular-nums text-right">{sizeLabel}</p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default EmailSignatureEditor;
